import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { categoryAPI } from '../../services/api';

interface CategoryMedia {
  id: string;
  categoryname: string;
  createdby: string;
  modifiedby: string | null;
  masterdetailId: string;
  categoryId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
}

interface Category {
  id: string;
  name: string;
  categorystatus: number; // 1=Active, 0=Inactive
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
  categorymedia: CategoryMedia[];
}

interface CategoriesState {
  categories: Category[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    items_per_page: number;
    total: number;
  };
  statistics: {
    total: number;
    active: number;
    deactive: number;
  };
  statisticsLoading: boolean;
  // Subcategory listing state
  subcategories: Category[];
  subcategoriesLoading: boolean;
  subcategoriesError: string | null;
  subcategoriesPagination: {
    page: number;
    items_per_page: number;
    total: number;
  };
}

const initialState: CategoriesState = {
  categories: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    items_per_page: 10,
    total: 0,
  },
  statistics: {
    total: 0,
    active: 0,
    deactive: 0,
  },
  statisticsLoading: false,
  // Subcategory listing state
  subcategories: [],
  subcategoriesLoading: false,
  subcategoriesError: null,
  subcategoriesPagination: {
    page: 1,
    items_per_page: 10,
    total: 0,
  },
};

// Fetch categories with pagination and filters
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (params?: { 
    page?: number; 
    items_per_page?: number; 
    search?: string; 
    'where[categorystatus]'?: number;
    sort?: string;
  }) => {
    const response = await categoryAPI.getCategories(params);
    
    // Handle new API response structure
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return {
        categories: response.data.data,
        pagination: response.data.payload || {
          page: 1,
          items_per_page: 10,
          total: 0,
        }
      };
    } else if (Array.isArray(response.data)) {
      return {
        categories: response.data,
        pagination: {
          page: 1,
          items_per_page: response.data.length,
          total: response.data.length,
        }
      };
    } else {
      return {
        categories: [],
        pagination: {
          page: 1,
          items_per_page: 10,
          total: 0,
        }
      };
    }
  }
);

// Fetch subcategories listing with pagination and filters
export const fetchSubcategoriesListing = createAsyncThunk(
  'categories/fetchSubcategoriesListing',
  async (params?: { 
    page?: number; 
    items_per_page?: number; 
    search?: string; 
    'where[categorystatus]'?: number;
    'where[parentId]'?: string;
    sort?: string; // Added sort parameter
  }) => {
    const response = await categoryAPI.getSubcategoriesListing(params);
    
    // Handle new API response structure
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return {
        subcategories: response.data.data,
        pagination: response.data.payload || {
          page: 1,
          items_per_page: 10,
          total: 0,
        }
      };
    } else if (Array.isArray(response.data)) {
      return {
        subcategories: response.data,
        pagination: {
          page: 1,
          items_per_page: response.data.length,
          total: response.data.length,
        }
      };
    } else {
      return {
        subcategories: [],
        pagination: {
          page: 1,
          items_per_page: 10,
          total: 0,
        }
      };
    }
  }
);

// Fetch category statistics
export const fetchCategoryStatistics = createAsyncThunk(
  'categories/fetchStatistics',
  async () => {
    const [activeResponse, deactiveResponse, totalResponse] = await Promise.all([
      categoryAPI.getActiveCategories(),
      categoryAPI.getDeactiveCategories(),
      categoryAPI.getTotalCategories(),
    ]);

    // Handle different response structures for statistics
    const getCount = (response: any) => {
      if (Array.isArray(response)) {
        return response.length;
      } else if (response && Array.isArray(response.data)) {
        return response.data.length;
      } else if (response && typeof response.count === 'number') {
        return response.count;
      } else {
        return 0;
      }
    };

    return {
      active: getCount(activeResponse.data),
      deactive: getCount(deactiveResponse.data),
      total: getCount(totalResponse.data),
    };
  }
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.categories;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch categories';
      })
      // Fetch Statistics
      .addCase(fetchCategoryStatistics.pending, (state) => {
        state.statisticsLoading = true;
      })
      .addCase(fetchCategoryStatistics.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchCategoryStatistics.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.error = action.error.message || 'Failed to fetch statistics';
      })
      // Fetch Subcategories Listing
      .addCase(fetchSubcategoriesListing.pending, (state) => {
        state.subcategoriesLoading = true;
        state.subcategoriesError = null;
      })
      .addCase(fetchSubcategoriesListing.fulfilled, (state, action) => {
        state.subcategoriesLoading = false;
        state.subcategories = action.payload.subcategories;
        state.subcategoriesPagination = action.payload.pagination;
      })
      .addCase(fetchSubcategoriesListing.rejected, (state, action) => {
        state.subcategoriesLoading = false;
        state.subcategoriesError = action.error.message || 'Failed to fetch subcategories';
      });
  },
});

export const { clearError } = categoriesSlice.actions;
export default categoriesSlice.reducer; 